import React, { useEffect, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, Factory, ExternalLink, Image as ImageIcon } from "lucide-react";
import ManufacturerFormDialog from "@/components/ManufacturerFormDialog";

export default function ManufacturersPage() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/manufacturers");
    setList(data);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Database
          </div>
          <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
            Manufacturers
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Each manufacturer holds its own product types and products. Start
            by adding a manufacturer, then define product types and their
            spec columns.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          data-testid="add-manufacturer-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add manufacturer
        </Button>
      </div>

      {list === null ? (
        <div className="byrd-progress" />
      ) : list.length === 0 ? (
        <EmptyState onAdd={() => setOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((m) => (
            <Link
              to={`/manufacturers/${m.id}`}
              key={m.id}
              data-testid={`manufacturer-card-${m.id}`}
              className="group bg-white border border-border rounded-sm p-5 hover:border-slate-900 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-border rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
                  {m.logo_photo_id ? (
                    <img src={photoUrl(m.logo_photo_id)} alt={m.name} className="w-full h-full object-contain" />
                  ) : (
                    <Factory className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-slate-900 group-hover:text-amber-600 transition-colors truncate">
                    {m.name}
                  </div>
                  {m.website ? (
                    <span
                      role="link"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const url = m.website.startsWith("http") ? m.website : `https://${m.website}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-slate-900 cursor-pointer"
                    >
                      {m.website} <ExternalLink className="w-3 h-3" />
                    </span>
                  ) : (
                    <div className="mt-1 text-xs text-muted-foreground">No website</div>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs font-mono-data">
                <span className="text-muted-foreground uppercase tracking-wider">
                  {m.product_type_count} types · {m.product_count} products
                </span>
                {m.product_count > 0 && (
                  <PhotoCoverageBadge
                    withPhotos={m.products_with_photos_count || 0}
                    total={m.product_count}
                  />
                )}
                <span className="text-amber-600 group-hover:translate-x-0.5 transition-transform">→</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <ManufacturerFormDialog
        open={open}
        onOpenChange={setOpen}
        mode="create"
        onSaved={() => load()}
      />
    </div>
  );
}

function PhotoCoverageBadge({ withPhotos, total }) {
  const pct = total > 0 ? Math.round((withPhotos / total) * 100) : 0;
  // color tiers: 0-19% red, 20-69% amber, 70-100% emerald
  const palette =
    pct >= 70
      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
      : pct >= 20
      ? "border-amber-300 bg-amber-50 text-amber-700"
      : "border-rose-300 bg-rose-50 text-rose-700";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${palette}`}
      title={`${withPhotos} of ${total} products have at least one photo`}
      data-testid="photo-coverage-badge"
    >
      <ImageIcon className="w-3 h-3" />
      {withPhotos}/{total}
    </span>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="border border-dashed border-border rounded-sm bg-white p-12 text-center" data-testid="manufacturers-empty">
      <div className="w-12 h-12 mx-auto bg-slate-900 text-white flex items-center justify-center rounded-sm">
        <Factory className="w-5 h-5" />
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
        No manufacturers yet
      </h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
        Add your first represented manufacturer to start building the
        searchable product database.
      </p>
      <Button onClick={onAdd} className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm">
        <Plus className="w-4 h-4 mr-2" /> Add manufacturer
      </Button>
    </div>
  );
}
